import type { DonationCampaign, Ticket } from "../../../types/reservation";

function toDateOnly(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

export function isDonationActive(
  donationCampaign: DonationCampaign | null | undefined,
): boolean {
  if (!donationCampaign) return false;
  const today = toDateOnly(new Date());
  const from = donationCampaign.activeFrom
    ? toDateOnly(new Date(donationCampaign.activeFrom))
    : null;
  const to = donationCampaign.activeTo
    ? toDateOnly(new Date(donationCampaign.activeTo))
    : null;
  return (!from || today >= from) && (!to || today <= to);
}

export function getDonationAmountBounds(
  donationCampaign: DonationCampaign,
  tickets: Ticket[],
): { min: number; max: number } {
  const ticketCount = tickets.length;
  const isPercent = donationCampaign.perTicketAmountIsInPercent;

  const min =
    donationCampaign.minAmountPerTicket != null &&
    donationCampaign.minAmountPerTicket > 0
      ? isPercent
        ? tickets.reduce(
            (sum, t) =>
              sum +
              (donationCampaign.minAmountPerTicket! / 100) * (t.price ?? 0),
            0,
          )
        : donationCampaign.minAmountPerTicket * ticketCount
      : (donationCampaign.minAmount ?? 0);

  const max =
    donationCampaign.maxAmountPerTicket != null &&
    donationCampaign.maxAmountPerTicket > 0
      ? isPercent
        ? tickets.reduce(
            (sum, t) =>
              sum +
              (donationCampaign.maxAmountPerTicket! / 100) * (t.price ?? 0),
            0,
          )
        : donationCampaign.maxAmountPerTicket * ticketCount
      : (donationCampaign.maxAmount ?? Infinity);

  return { min, max };
}

export function computeDonationDefaultAmount(
  donationCampaign: DonationCampaign,
  tickets: Ticket[],
  ticketsTotal: number,
): number {
  const ticketCount = tickets.length;

  const hasMinAmountPerTicket =
    donationCampaign.minAmountPerTicket != null &&
    donationCampaign.minAmountPerTicket > 0;

  const hasMinAmount =
    donationCampaign.minAmount != null && donationCampaign.minAmount > 0;

  const isPerTicketAmountInPercent =
    donationCampaign.perTicketAmountIsInPercent && hasMinAmountPerTicket;

  const isAmountInPercent = donationCampaign.amountIsInPercent && hasMinAmount;

  const computed = isPerTicketAmountInPercent
    ? tickets.reduce(
        (sum, t) =>
          sum + (donationCampaign.minAmountPerTicket! / 100) * (t.price ?? 0),
        0,
      )
    : hasMinAmountPerTicket
      ? donationCampaign.minAmountPerTicket! * ticketCount
      : isAmountInPercent
        ? (donationCampaign.defaultAmount / 100) * ticketsTotal
        : donationCampaign.defaultAmount;

  const isPercentageBased = isAmountInPercent || isPerTicketAmountInPercent;
  const rawDefault = donationCampaign.defaultAmount ?? 0;

  return !isPercentageBased && computed < rawDefault ? rawDefault : computed;
}
